import { Alert, Button, CircularProgress, Grid, Typography } from '@mui/material';
import { useEffect } from 'react';
import { PvAnalysisBuildingMetadata } from '../pv-analysis.dto';
import { useTranslation } from 'react-i18next';
import { importBuilding, setErrorMessage, setSuccessMessage } from '../pv-analysis.slice';
import { useAppDispatch, useAppSelector } from 'src/redux-app-hooks';
import { useNavigate } from 'react-router';

type Props = {
    onImportSuccess?: () => void;
    hideReturnButton?: boolean;
};

export default function SelectedBuildingMetadata({ onImportSuccess, hideReturnButton }: Props = {}) {
    const { selectedBuildingMetadata, errorMessage, successMessage, isImportBuildingLoading } = useAppSelector(
        (state) => state.pvAnalysis,
    );

    const dispatch = useAppDispatch();

    const { t } = useTranslation();

    const navigate = useNavigate();

    useEffect(() => {
        dispatch(setErrorMessage(''));
        dispatch(setSuccessMessage(''));
    }, [dispatch]);

    async function importNewBuilding(buildingMetadata: PvAnalysisBuildingMetadata) {
        const result = await dispatch(importBuilding(buildingMetadata));
        if (importBuilding.fulfilled.match(result)) {
            onImportSuccess?.();
        }
    }

    return (
        <Grid size={12} container spacing={3}>
            {isImportBuildingLoading && <CircularProgress />}

            {selectedBuildingMetadata === null && (
                <>
                    <Grid size={12}>
                        <Alert>{t('Please, select a building')}.</Alert>
                    </Grid>
                    {!hideReturnButton && (
                        <Grid size={12}>
                            <Button variant="outlined" onClick={() => navigate(-1)}>
                                {t('Return to the previous screen')}
                            </Button>
                        </Grid>
                    )}
                </>
            )}
            {selectedBuildingMetadata !== null && (
                <Grid key={selectedBuildingMetadata.code} size={12} container>
                    <Grid size={11.5}>
                        <Typography variant="h5">
                            {t('Building')} #({selectedBuildingMetadata.code})
                        </Typography>
                    </Grid>
                    <Grid container direction="row" spacing={1}>
                        <Grid size={5}>
                            <b>{t('Building Function')}:</b>
                        </Grid>
                        <Grid size={6}>{selectedBuildingMetadata.function}</Grid>
                    </Grid>
                    <Grid container direction="row" spacing={1}>
                        <Grid size={5}>
                            <b>{t('Building File Path')}:</b>
                        </Grid>
                        <Grid size={6}>{selectedBuildingMetadata.filePath}</Grid>
                    </Grid>
                    <Grid size={12} container sx={{ alignContent: 'center' }}>
                        <Grid size={6}>
                            <Button
                                variant="contained"
                                loading={isImportBuildingLoading}
                                onClick={() => importNewBuilding(selectedBuildingMetadata)}
                            >
                                {t('Import Building')}
                            </Button>
                        </Grid>
                        {!hideReturnButton && (
                            <Grid size={6}>
                                <Button variant="outlined" onClick={() => navigate(-1)}>
                                    {t('Return to the previous screen')}
                                </Button>
                            </Grid>
                        )}
                    </Grid>
                    {errorMessage && (
                        <Grid size={12}>
                            <Alert color="error">{t(errorMessage)}</Alert>
                        </Grid>
                    )}
                    {successMessage && (
                        <Grid size={12}>
                            <Alert color="success">{t(successMessage)}</Alert>
                        </Grid>
                    )}
                </Grid>
            )}
        </Grid>
    );
}
